import axios from 'axios';
import { Globals } from '@/modules/Globals';
import { IPaginated } from '@/interfaces/IPaginated';

const apiUrl = Globals.apiPath + '/v2/table-sessions';

export type TSessionInvoiceState =
  | 'invoiced'
  | 'partial'
  | 'pending'
  | 'comped';

/** Row of GET v2/table-sessions/closed (history "Mesas" tab). */
export interface IClosedTableSession {
  id: string;
  tableId: number;
  tableName: string;
  closedAt: string;
  ordersCount: number;
  total: number;
  invoiceState: TSessionInvoiceState;
}

export interface IGetClosedSessionsFilters {
  /**
   * ISO instants (always via toISOString()). Applied on closedAt; dateTo is
   * exclusive. The server applies a 90-day fallback lower bound when
   * dateFrom is absent.
   */
  dateFrom?: string;
  dateTo?: string;
  /** Search by table name (includes soft-deleted tables) or customer name. */
  search?: string;
  /** Explicit table filter (Mesas popover multi-selection). */
  tableIds?: number[];
  page?: number;
  itemsPerPage?: number;
}

/**
 * Closed table sessions for the unified orders history. NOTE: the two
 * pre-existing table-session calls (covers patch, orders of a session) live
 * in orders.service.ts — deliberately left there; this service is home only
 * to the closed-sessions listing.
 */
export default class TableSessionsService {
  static async getClosedSessions(
    restaurantId: number,
    branchId: number,
    filters: IGetClosedSessionsFilters = {}
  ): Promise<IPaginated<IClosedTableSession>> {
    let path = `${apiUrl}/closed?r=${restaurantId}&b=${branchId}`;

    if (filters.dateFrom)
      path += `&dateFrom=${encodeURIComponent(filters.dateFrom)}`;
    if (filters.dateTo) path += `&dateTo=${encodeURIComponent(filters.dateTo)}`;
    if (filters.search && filters.search.trim())
      path += `&search=${encodeURIComponent(filters.search.trim())}`;
    if (filters.tableIds && filters.tableIds.length > 0)
      path += `&tableIds=${filters.tableIds.join(',')}`;
    if (filters.page || filters.itemsPerPage) {
      path += `&options=${JSON.stringify({
        page: filters.page ?? 1,
        itemsPerPage: filters.itemsPerPage ?? 50,
      })}`;
    }

    const res = await axios.get<IPaginated<IClosedTableSession>>(path);
    return res.data;
  }
}
